<script setup>
// Marcar, ver, editar, remarcar e cancelar uma consulta (ver AGENDA.md).
// Os horários livres vêm do servidor (mesma regra da IA); a recepção ainda pode digitar
// outro horário para um encaixe. Horário ocupado volta como erro 409 e aparece aqui.
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import agendaAPI from 'dashboard/api/agenda';
import ContactAPI from 'dashboard/api/contacts';
import { useAgendaData } from '../composables/useAgendaData';
import {
  addDays,
  datePart,
  formatDayLabel,
  minutesOfDay,
  minutesToTime,
  nowMinuteIn,
  todayIn,
  weekdayOf,
} from '../helpers/agendaTime';

const emit = defineEmits(['saved']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { setting, activeProfessionals, professionalById, periodsFor } =
  useAgendaData();

const SEARCH_DELAY = 300;
const TIME_FORMAT = /^\d{2}:\d{2}$/;

const emptyForm = () => ({
  professional_id: '',
  date: '',
  time: '',
  patient_name: '',
  patient_phone: '',
  patient_email: '',
  insurance: '',
  notes: '',
});

const dialog = ref(null);
const appointment = ref(null);
const form = ref(emptyForm());
const contact = ref(null);
const slots = ref([]);
const isLoadingSlots = ref(false);
const isSaving = ref(false);
const error = ref('');
const cancelling = ref(false);
const cancelReason = ref('');
const contactQuery = ref('');
const contactResults = ref([]);
const hasSearched = ref(false);
let searchTimer = null;
let slotsRequest = 0;

const isEdit = computed(() => Boolean(appointment.value));
const isCancelled = computed(() => appointment.value?.status === 'cancelled');
const professional = computed(() =>
  professionalById(form.value.professional_id)
);

const professionalOptions = computed(() => {
  const options = activeProfessionals.value.map(item => ({
    value: item.id,
    label: `${item.name} · ${item.specialty}`,
  }));
  const current = appointment.value;
  if (current && !options.some(o => o.value === current.professional_id)) {
    options.push({
      value: current.professional_id,
      label: current.professional_name,
    });
  }
  return options;
});

const startTimeOf = item => minutesToTime(minutesOfDay(item.starts_at));
const slotTimes = computed(() => slots.value.map(startTimeOf));

const scheduleUnchanged = computed(() => {
  const current = appointment.value;
  return (
    Boolean(current) &&
    Number(form.value.professional_id) === current.professional_id &&
    form.value.date === datePart(current.starts_at) &&
    form.value.time === startTimeOf(current)
  );
});

// "Agora" no fuso da agenda, atualizado ao abrir a janela.
const now = ref(new Date());
const today = computed(() => todayIn(setting.value.timezone, now.value));
const nowTime = computed(() =>
  minutesToTime(nowMinuteIn(setting.value.timezone, now.value))
);
const isPastDate = computed(
  () => Boolean(form.value.date) && form.value.date < today.value
);
const isToday = computed(() => form.value.date === today.value);

// Novo horário (não a consulta como já estava) em data passada ou hoje antes de agora.
const isPastSelection = computed(
  () =>
    !scheduleUnchanged.value &&
    (isPastDate.value ||
      (isToday.value &&
        TIME_FORMAT.test(form.value.time) &&
        form.value.time <= nowTime.value))
);

// Horário de atendimento do profissional no dia escolhido ("08:00–12:00, 14:00–18:00").
const dayHours = computed(() => {
  if (!professional.value || !form.value.date) return '';
  return periodsFor(professional.value, weekdayOf(form.value.date))
    .map(p => `${minutesToTime(p.start)}–${minutesToTime(p.end)}`)
    .join(', ');
});

const minNoticeHours = computed(() =>
  Math.round(setting.value.min_notice_minutes / 60)
);

const isOffGrid = computed(
  () =>
    TIME_FORMAT.test(form.value.time) &&
    !isLoadingSlots.value &&
    !scheduleUnchanged.value &&
    !isPastSelection.value &&
    !slotTimes.value.includes(form.value.time)
);

const canSave = computed(
  () =>
    Boolean(form.value.professional_id) &&
    Boolean(form.value.date) &&
    TIME_FORMAT.test(form.value.time) &&
    !isPastSelection.value &&
    form.value.patient_name.trim().length > 0
);

const title = computed(() =>
  isEdit.value ? t('AGENDA.APPOINTMENT.EDIT_TITLE') : t('AGENDA.APPOINTMENT.NEW_TITLE')
);

const sourceLabel = computed(() => {
  const current = appointment.value;
  if (!current) return '';
  const source =
    current.source === 'ai'
      ? t('AGENDA.APPOINTMENT.SOURCE_AI')
      : t('AGENDA.APPOINTMENT.SOURCE_PANEL');
  return current.created_by
    ? `${source} ${t('AGENDA.APPOINTMENT.CREATED_BY', { name: current.created_by.name })}`
    : source;
});

const fetchSlots = async () => {
  slots.value = [];
  const { professional_id: professionalId, date } = form.value;
  if (!professionalId || !date || isCancelled.value) return;

  slotsRequest += 1;
  const request = slotsRequest;
  isLoadingSlots.value = true;
  try {
    const { data } = await agendaAPI.getSlots({
      professional_id: professionalId,
      from: date,
      to: addDays(date, 1),
    });
    if (request === slotsRequest) slots.value = data.payload;
  } catch {
    if (request === slotsRequest) slots.value = [];
  } finally {
    if (request === slotsRequest) isLoadingSlots.value = false;
  }
};

watch(() => [form.value.professional_id, form.value.date], fetchSlots);

const searchContacts = () => {
  clearTimeout(searchTimer);
  const query = contactQuery.value.trim();
  if (query.length < 2) {
    contactResults.value = [];
    hasSearched.value = false;
    return;
  }
  searchTimer = setTimeout(async () => {
    try {
      const { data } = await ContactAPI.search(query);
      contactResults.value = data.payload.slice(0, 6);
    } catch {
      contactResults.value = [];
    } finally {
      hasSearched.value = true;
    }
  }, SEARCH_DELAY);
};

const pickContact = item => {
  contact.value = { id: item.id, name: item.name };
  form.value.patient_name = item.name || form.value.patient_name;
  form.value.patient_phone = item.phone_number || form.value.patient_phone;
  form.value.patient_email = item.email || form.value.patient_email;
  contactQuery.value = '';
  contactResults.value = [];
  hasSearched.value = false;
};

const resetState = () => {
  error.value = '';
  cancelling.value = false;
  cancelReason.value = '';
  contactQuery.value = '';
  contactResults.value = [];
  hasSearched.value = false;
  slots.value = [];
};

// open({ appointment }) para ver/editar; open({ professionalId, date, time }) para marcar.
const open = ({ appointment: existing, professionalId, date, time } = {}) => {
  resetState();
  now.value = new Date();
  if (existing) {
    appointment.value = existing;
    form.value = {
      professional_id: existing.professional_id,
      date: datePart(existing.starts_at),
      time: startTimeOf(existing),
      patient_name: existing.patient_name || '',
      patient_phone: existing.patient_phone || '',
      patient_email: existing.patient_email || '',
      insurance: existing.insurance || '',
      notes: existing.notes || '',
    };
    contact.value = existing.contact_id
      ? { id: existing.contact_id, name: existing.patient_name }
      : null;
  } else {
    const onlyOne =
      activeProfessionals.value.length === 1 ? activeProfessionals.value[0].id : '';
    appointment.value = null;
    contact.value = null;
    form.value = {
      ...emptyForm(),
      professional_id: professionalId || onlyOne,
      date: date || todayIn(setting.value.timezone),
      time: time || '',
    };
  }
  dialog.value?.open();
};

const patientPayload = () => ({
  patient_name: form.value.patient_name.trim(),
  patient_phone: form.value.patient_phone.trim(),
  patient_email: form.value.patient_email.trim(),
  insurance: form.value.insurance.trim(),
  notes: form.value.notes.trim(),
  ...(contact.value ? { contact_id: contact.value.id } : {}),
});

const finish = (data, message) => {
  useAlert(message);
  emit('saved', data);
  dialog.value?.close();
};

const save = async () => {
  if (!canSave.value || isSaving.value || isCancelled.value) return;
  isSaving.value = true;
  error.value = '';
  const startsAt = `${form.value.date}T${form.value.time}`;
  try {
    if (isEdit.value) {
      const payload = patientPayload();
      if (!scheduleUnchanged.value) {
        payload.professional_id = form.value.professional_id;
        payload.starts_at = startsAt;
      }
      const { data } = await agendaAPI.updateAppointment(appointment.value.id, payload);
      finish(data, t('AGENDA.APPOINTMENT.UPDATED'));
    } else {
      const { data } = await agendaAPI.createAppointment({
        professional_id: form.value.professional_id,
        starts_at: startsAt,
        ...patientPayload(),
      });
      finish(data, t('AGENDA.APPOINTMENT.CREATED'));
    }
  } catch (requestError) {
    error.value =
      requestError?.response?.data?.message || t('AGENDA.APPOINTMENT.ERROR');
  } finally {
    isSaving.value = false;
  }
};

const confirmCancel = async () => {
  if (isSaving.value) return;
  isSaving.value = true;
  error.value = '';
  try {
    const { data } = await agendaAPI.cancelAppointment(
      appointment.value.id,
      cancelReason.value.trim()
    );
    finish(data, t('AGENDA.APPOINTMENT.CANCELLED_SUCCESS'));
  } catch (requestError) {
    error.value =
      requestError?.response?.data?.message || t('AGENDA.APPOINTMENT.ERROR');
  } finally {
    isSaving.value = false;
  }
};

const onSubmit = () => (cancelling.value ? confirmCancel() : save());

const openConversation = () => {
  dialog.value?.close();
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: appointment.value.conversation_display_id,
    },
  });
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="title"
    width="2xl"
    position="top"
    overflow-y-auto
    @confirm="onSubmit"
  >
    <div class="flex flex-col gap-5" data-testid="agenda-appointment-dialog">
      <div
        v-if="isEdit"
        class="flex flex-wrap items-center gap-2 text-label-small text-n-slate-11"
      >
        <span
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-n-alpha-2"
        >
          <span
            :class="
              appointment.source === 'ai'
                ? 'i-lucide-sparkles text-n-brand'
                : 'i-lucide-user-round'
            "
            class="size-3"
          />
          {{ sourceLabel }}
        </span>
        <Button
          v-if="appointment.conversation_display_id"
          type="button"
          :label="t('AGENDA.APPOINTMENT.OPEN_CONVERSATION')"
          icon="i-lucide-message-circle"
          variant="link"
          size="sm"
          @click="openConversation"
        />
      </div>

      <div
        v-if="isCancelled"
        class="flex flex-col gap-1 px-4 py-3 rounded-xl bg-n-ruby-2 outline outline-1 outline-n-ruby-5 text-n-ruby-11 text-sm"
      >
        <span class="font-medium">{{ t('AGENDA.APPOINTMENT.CANCELLED') }}</span>
        <span v-if="appointment.cancellation_reason">
          {{
            t('AGENDA.APPOINTMENT.CANCELLED_REASON', {
              reason: appointment.cancellation_reason,
            })
          }}
        </span>
      </div>

      <!-- Quando -->
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-2">
          <label class="text-heading-3 text-n-slate-12">
            {{ t('AGENDA.APPOINTMENT.PROFESSIONAL') }}
          </label>
          <Select
            v-model="form.professional_id"
            :options="professionalOptions"
            :placeholder="t('AGENDA.APPOINTMENT.SELECT_PROFESSIONAL')"
            :disabled="isCancelled"
          />
        </div>
        <div class="grid gap-3 sm:grid-cols-2">
          <Input
            v-model="form.date"
            type="date"
            :min="isEdit ? '' : today"
            :label="t('AGENDA.APPOINTMENT.DATE')"
            :disabled="isCancelled"
          />
          <Input
            v-model="form.time"
            type="time"
            step="300"
            :label="t('AGENDA.APPOINTMENT.TIME')"
            :message="
              professional
                ? t('AGENDA.APPOINTMENT.DURATION', {
                    minutes: professional.duration_minutes,
                  })
                : ''
            "
            :disabled="isCancelled"
          />
        </div>

        <div
          v-if="!isCancelled && form.professional_id && form.date"
          class="flex flex-col gap-2"
        >
          <span class="text-label-small text-n-slate-11">
            {{ t('AGENDA.APPOINTMENT.FREE_SLOTS') }} ·
            {{ formatDayLabel(form.date) }}
          </span>
          <span
            class="text-label-small text-n-slate-11"
            data-testid="agenda-day-hours"
          >
            {{
              dayHours
                ? t('AGENDA.APPOINTMENT.WORKING_HOURS', { hours: dayHours })
                : t('AGENDA.APPOINTMENT.NOT_WORKING')
            }}
          </span>
          <span v-if="isLoadingSlots" class="text-label-small text-n-slate-10">
            {{ t('AGENDA.APPOINTMENT.LOADING_SLOTS') }}
          </span>
          <span
            v-else-if="isPastDate && !scheduleUnchanged"
            class="text-label-small text-n-ruby-11"
            data-testid="agenda-past-message"
          >
            {{ t('AGENDA.APPOINTMENT.PAST_DATE') }}
          </span>
          <span
            v-else-if="!slots.length && isToday"
            class="text-label-small text-n-slate-10"
          >
            {{ t('AGENDA.APPOINTMENT.NO_SLOTS_TODAY', { hours: minNoticeHours }) }}
          </span>
          <span v-else-if="!slots.length" class="text-label-small text-n-slate-10">
            {{ t('AGENDA.APPOINTMENT.NO_SLOTS') }}
          </span>
          <div v-else class="flex flex-wrap gap-1.5" data-testid="agenda-slots">
            <button
              v-for="time in slotTimes"
              :key="time"
              type="button"
              class="px-2.5 py-1 text-sm rounded-lg tabular-nums outline outline-1 transition-colors"
              :class="
                form.time === time
                  ? 'bg-n-brand text-white outline-n-brand'
                  : 'bg-n-solid-1 text-n-slate-12 outline-n-weak hover:outline-n-brand'
              "
              @click="form.time = time"
            >
              {{ time }}
            </button>
          </div>
          <span
            v-if="isPastSelection && !isPastDate"
            class="text-label-small text-n-ruby-11"
            data-testid="agenda-past-message"
          >
            {{ t('AGENDA.APPOINTMENT.PAST_TIME') }}
          </span>
          <span v-if="isOffGrid" class="text-label-small text-n-amber-11">
            {{ t('AGENDA.APPOINTMENT.OFF_GRID') }}
          </span>
        </div>
      </div>

      <!-- Paciente -->
      <div class="flex flex-col gap-3 pt-4 border-t border-n-weak">
        <span class="text-heading-3 text-n-slate-12">
          {{ t('AGENDA.APPOINTMENT.PATIENT') }}
        </span>

        <div v-if="!isCancelled && !contact" class="relative">
          <Input
            v-model="contactQuery"
            :placeholder="t('AGENDA.APPOINTMENT.SEARCH_CONTACT_PLACEHOLDER')"
            :aria-label="t('AGENDA.APPOINTMENT.SEARCH_CONTACT')"
            custom-input-class="!ps-9"
            @input="searchContacts"
          >
            <template #prefix>
              <span
                class="absolute i-lucide-search size-4 text-n-slate-10 start-3 top-3"
              />
            </template>
          </Input>
          <ul
            v-if="contactResults.length || hasSearched"
            class="absolute z-10 w-full mt-1 overflow-hidden rounded-lg shadow-lg bg-n-solid-1 outline outline-1 outline-n-weak"
          >
            <li
              v-if="!contactResults.length"
              class="px-3 py-2 text-sm text-n-slate-11"
            >
              {{ t('AGENDA.APPOINTMENT.NO_CONTACTS') }}
            </li>
            <li v-for="item in contactResults" :key="item.id">
              <button
                type="button"
                class="flex flex-col w-full px-3 py-2 text-left hover:bg-n-alpha-2"
                @click="pickContact(item)"
              >
                <span class="text-sm text-n-slate-12">{{ item.name }}</span>
                <span class="text-label-small text-n-slate-11">
                  {{ [item.phone_number, item.email].filter(Boolean).join(' · ') }}
                </span>
              </button>
            </li>
          </ul>
        </div>
        <div
          v-else-if="contact"
          class="flex items-center gap-2 text-label-small text-n-slate-11"
        >
          <span class="i-lucide-link size-3" />
          {{ t('AGENDA.APPOINTMENT.LINKED_CONTACT', { name: contact.name }) }}
          <Button
            v-if="!isEdit"
            type="button"
            :label="t('AGENDA.APPOINTMENT.UNLINK')"
            variant="link"
            size="sm"
            @click="contact = null"
          />
        </div>

        <div class="grid gap-3 sm:grid-cols-2">
          <Input
            v-model="form.patient_name"
            :label="t('AGENDA.APPOINTMENT.PATIENT_NAME')"
            :disabled="isCancelled"
          />
          <Input
            v-model="form.patient_phone"
            type="tel"
            :label="t('AGENDA.APPOINTMENT.PATIENT_PHONE')"
            :disabled="isCancelled"
          />
          <Input
            v-model="form.patient_email"
            type="email"
            :label="t('AGENDA.APPOINTMENT.PATIENT_EMAIL')"
            :disabled="isCancelled"
          />
          <Input
            v-model="form.insurance"
            :label="t('AGENDA.APPOINTMENT.INSURANCE')"
            :placeholder="t('AGENDA.APPOINTMENT.INSURANCE_PLACEHOLDER')"
            :disabled="isCancelled"
          />
        </div>
        <TextArea
          v-model="form.notes"
          :label="t('AGENDA.APPOINTMENT.NOTES')"
          :placeholder="t('AGENDA.APPOINTMENT.NOTES_PLACEHOLDER')"
          :max-length="2000"
          :disabled="isCancelled"
          auto-height
        />
      </div>

      <div
        v-if="cancelling"
        class="flex flex-col gap-2 px-4 py-3 rounded-xl bg-n-ruby-2 outline outline-1 outline-n-ruby-5"
      >
        <Input
          v-model="cancelReason"
          :label="t('AGENDA.APPOINTMENT.CANCEL_REASON')"
          autofocus
        />
      </div>

      <p
        v-if="error"
        class="px-4 py-3 mb-0 text-sm rounded-xl bg-n-ruby-2 text-n-ruby-11"
        data-testid="agenda-appointment-error"
      >
        {{ error }}
      </p>
    </div>

    <template #footer>
      <div class="flex flex-wrap items-center justify-between w-full gap-3">
        <div>
          <Button
            v-if="isEdit && !isCancelled && !cancelling"
            type="button"
            :label="t('AGENDA.APPOINTMENT.CANCEL')"
            icon="i-lucide-calendar-x"
            variant="ghost"
            color="ruby"
            size="sm"
            @click="cancelling = true"
          />
        </div>
        <div class="flex items-center gap-2">
          <template v-if="cancelling">
            <Button
              type="button"
              :label="t('AGENDA.APPOINTMENT.CANCEL_BACK')"
              variant="faded"
              color="slate"
              @click="cancelling = false"
            />
            <Button
              type="submit"
              :label="t('AGENDA.APPOINTMENT.CANCEL_CONFIRM')"
              color="ruby"
              :is-loading="isSaving"
            />
          </template>
          <template v-else>
            <Button
              type="button"
              :label="t('AGENDA.APPOINTMENT.CLOSE')"
              variant="faded"
              color="slate"
              @click="dialog?.close()"
            />
            <Button
              v-if="!isCancelled"
              type="submit"
              :label="
                isEdit ? t('AGENDA.APPOINTMENT.UPDATE') : t('AGENDA.APPOINTMENT.SAVE')
              "
              :is-loading="isSaving"
              :disabled="!canSave"
              data-testid="agenda-appointment-save"
            />
          </template>
        </div>
      </div>
    </template>
  </Dialog>
</template>
